import { useEffect, useState } from "react";
import { Plus, Pencil, Power } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const empty = { store_code: "", store_name: "", project_id: "", location: "" };

export default function StoreMaster() {
  const { has } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [refresh, setRefresh] = useState(0);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects", { params: { page_size: 500 } }).then((r) => setProjects(r.data.items || [])).catch(() => {});
  }, [refresh]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...empty, ...row }); setOpen(true); };

  const save = async () => {
    if (!form.store_code.trim() || !form.store_name.trim() || !form.project_id) {
      toast.error("Store code, name and project are required");
      return;
    }
    try {
      if (editing) {
        await api.put(`/stores/${editing.id}`, { store_name: form.store_name, location: form.location, project_id: form.project_id });
        toast.success("Store updated");
      } else {
        await api.post("/stores", form);
        toast.success("Store created");
      }
      setOpen(false);
      setRefresh((n) => n + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const toggle = async (row) => {
    try {
      await api.patch(`/stores/${row.id}/status?is_active=${!row.is_active}`);
      setRefresh((n) => n + 1);
    } catch { toast.error("Action failed"); }
  };

  const columns = [
    { key: "store_code", label: "Code", mono: true },
    { key: "store_name", label: "Store / Warehouse", render: (r) => <span className="font-medium text-slate-900">{r.store_name}</span> },
    { key: "project_name", label: "Project" },
    { key: "location", label: "Location", hideMobile: true },
    { key: "status", label: "Status", render: (r) => <Badge className={r.is_active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-200 text-slate-600"}>{r.is_active ? "Active" : "Inactive"}</Badge> },
  ];

  return (
    <div>
      <PageHeader title="Store / Warehouse Master" subtitle="Stores belong to a project; users assign many-to-many" actions={
        has("STORE.CREATE") && <Button data-testid="store-add" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700"><Plus className="mr-1.5 h-4 w-4" /> New Store</Button>
      } />

      <DataTable
        testid="stores-table"
        columns={columns}
        searchPlaceholder="Search by code or name..."
        refreshKey={refresh}
        filters={[{ key: "project_id", label: "Project", options: projects.map((p) => ({ value: p.id, label: p.project_name })) }]}
        load={(params) => api.get("/stores", { params }).then((r) => r.data)}
        rowActions={(row) => (
          <div className="flex justify-end gap-1">
            {has("STORE.EDIT") && <Button data-testid={`store-edit-${row.id}`} variant="ghost" size="sm" onClick={() => openEdit(row)}><Pencil className="h-4 w-4" /></Button>}
            {has("STORE.DEACTIVATE") && <Button data-testid={`store-toggle-${row.id}`} variant="ghost" size="sm" onClick={() => toggle(row)}><Power className={`h-4 w-4 ${row.is_active ? "text-red-500" : "text-emerald-500"}`} /></Button>}
          </div>
        )}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Store" : "New Store"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Store Code *</Label><Input data-testid="store-code-input" disabled={!!editing} value={form.store_code} onChange={(e) => setForm({ ...form, store_code: e.target.value })} className="mt-1" /></div>
              <div><Label>Store Name *</Label><Input data-testid="store-name-input" value={form.store_name} onChange={(e) => setForm({ ...form, store_name: e.target.value })} className="mt-1" /></div>
            </div>
            <div>
              <Label>Project *</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="store-project-select" className="mt-1"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.project_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="store-save" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
